import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Bell, CheckCircle, AlertTriangle, Eye, MessageSquare, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyScope } from '../context/CompanyScopeContext';

export default function Alerts() {
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [comment, setComment] = useState('');
  const [dialogAction, setDialogAction] = useState(null);
  const [checking, setChecking] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  useEffect(() => {
    fetchAlerts();
    fetchSchedulerStatus();
  }, []);

  const fetchSchedulerStatus = async () => {
    try {
      const response = await api.get('/scheduler/status');
      setSchedulerStatus(response.data);
    } catch (e) {
      // silent (HR-only endpoint)
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts', { params: { status: 'all' } });
      setAlerts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedAlert || !dialogAction) return;
    try {
      await api.post(`/alerts/${selectedAlert.id}/${dialogAction}`, null, {
        params: { comment: comment || undefined }
      });
      toast.success(dialogAction === 'acknowledge' ? 'Alerte acquittee' : 'Alerte resolue');
      setSelectedAlert(null);
      setComment('');
      setDialogAction(null);
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors du traitement');
    }
  };

  const handleCheckExceptions = async () => {
    setChecking(true);
    try {
      const response = await api.post('/alerts/check-exceptions');
      const data = response.data;
      if (data.alerts_created > 0) {
        toast.success(`${data.alerts_created} nouvelle(s) alerte(s) creee(s)`);
      } else {
        toast.info('Aucune nouvelle alerte detectee');
      }
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la verification');
    } finally {
      setChecking(false);
    }
  };

  const handleCheckLateThreshold = async () => {
    setChecking(true);
    try {
      const response = await api.post('/alerts/late-threshold/check');
      const { sent, skipped, errors } = response.data;
      if (sent > 0) {
        toast.success(`${sent} employe(s) notifie(s) (retards mensuels)`);
      } else {
        toast.info(`Aucun seuil de retard depasse (${skipped} skipped, ${errors} erreurs)`);
      }
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Erreur scan retards');
    } finally {
      setChecking(false);
    }
  };

  const openDialog = (alert, action) => {
    setSelectedAlert(alert);
    setDialogAction(action);
    setComment('');
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { label: 'Active', className: 'bg-red-100 text-red-700 border-red-200' },
      acknowledged: { label: 'Acquittee', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      resolved: { label: 'Resolue', className: 'bg-green-100 text-green-700 border-green-200' }
    };
    const v = map[status] || map.active;
    return <Badge variant="outline" className={v.className}>{v.label}</Badge>;
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical') return <AlertTriangle className="w-5 h-5 text-red-500" />;
    return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  };

  // Filtre par entreprise (doit être avant tout early return pour respecter les règles des hooks)
  const scopedAlerts = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return alerts;
    return alerts.filter(a => a.company_id === selectedCompanyId);
  }, [alerts, selectedCompanyId, isCrossCompany]);

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  const activeAlerts = scopedAlerts.filter(a => a.status === 'active');
  const acknowledgedAlerts = scopedAlerts.filter(a => a.status === 'acknowledged');
  const resolvedAlerts = scopedAlerts.filter(a => a.status === 'resolved');

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Alertes</h1>
          <p className="text-slate-600 mt-1">Surveillance automatique des anomalies</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCheckLateThreshold}
            disabled={checking}
            variant="outline"
            data-testid="check-late-threshold-btn"
          >
            <Bell className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            Scanner retards mensuels
          </Button>
          <Button
            onClick={handleCheckExceptions}
            disabled={checking}
            variant="outline"
            data-testid="check-exceptions-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            Verifier les exceptions
          </Button>
        </div>
      </div>

      <SchedulerStatusBar status={schedulerStatus} />

      {activeAlerts.length === 0 && acknowledgedAlerts.length === 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <p className="text-green-700 font-medium">Aucune alerte active</p>
            <p className="text-green-600 text-sm mt-1">Tout est en ordre.</p>
          </CardContent>
        </Card>
      )}

      {activeAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
            <Bell className="w-5 h-5" /> Alertes actives ({activeAlerts.length})
          </h2>
          <div className="space-y-3">
            {activeAlerts.map(alert => (
              <Card key={alert.id} className="border-red-200 bg-red-50/50" data-testid={`alert-${alert.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getSeverityIcon(alert.severity)}
                      <div>
                        <p className="font-semibold text-slate-900">{alert.title}</p>
                        <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                        <div className="flex items-center gap-3 mt-2">
                          {getStatusBadge(alert.status)}
                          <span className="text-xs text-slate-400">
                            {new Date(alert.created_at).toLocaleString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openDialog(alert, 'acknowledge')} data-testid={`ack-${alert.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Acquitter
                      </Button>
                      <Button size="sm" onClick={() => openDialog(alert, 'resolve')} data-testid={`resolve-${alert.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Resoudre
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {acknowledgedAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-amber-700 mb-3 flex items-center gap-2">
            <Eye className="w-5 h-5" /> Alertes acquittees ({acknowledgedAlerts.length})
          </h2>
          <div className="space-y-3">
            {acknowledgedAlerts.map(alert => (
              <Card key={alert.id} className="border-amber-200 bg-amber-50/30" data-testid={`alert-${alert.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Eye className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="font-semibold text-slate-900">{alert.title}</p>
                        <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                        {alert.comment && (
                          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> {alert.comment}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          {getStatusBadge(alert.status)}
                          <span className="text-xs text-slate-400">
                            Acquittee le {new Date(alert.acknowledged_at).toLocaleString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => openDialog(alert, 'resolve')} data-testid={`resolve-${alert.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Resoudre
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {resolvedAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" /> Historique resolu ({resolvedAlerts.length})
          </h2>
          <div className="space-y-2">
            {resolvedAlerts.slice(0, 10).map(alert => (
              <Card key={alert.id} className="border-slate-200 bg-slate-50/50 opacity-70" data-testid={`alert-${alert.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{alert.title}</p>
                      <span className="text-xs text-slate-400">
                        Resolu le {new Date(alert.resolved_at).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    {getStatusBadge(alert.status)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!dialogAction} onOpenChange={(open) => { if (!open) { setDialogAction(null); setSelectedAlert(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogAction === 'acknowledge' ? 'Acquitter l\'alerte' : 'Resoudre l\'alerte'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{selectedAlert?.title}</p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Commentaire (optionnel)..."
              rows={3}
              data-testid="alert-comment-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogAction(null)} className="flex-1">Annuler</Button>
              <Button onClick={handleAction} className="flex-1" data-testid="alert-confirm-btn">
                {dialogAction === 'acknowledge' ? 'Acquitter' : 'Resoudre'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function timeAgo(iso) {
  if (!iso) return 'jamais';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "a l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

function nextRunLabel(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const JOB_LABELS = {
  late_threshold_scan: 'Scan retards mensuels',
  attendance_backfill: 'Backfill presences',
  cleanup_late_alerts: 'Nettoyage alertes anciennes',
};

function SchedulerStatusBar({ status }) {
  if (!status || !status.running) return null;
  const runsByJob = {};
  (status.runs || []).forEach((r) => { runsByJob[r.job_name] = r; });
  return (
    <Card className="border-slate-200 bg-slate-50" data-testid="scheduler-status">
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Planificateur automatique</span>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
            Actif
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          {(status.jobs || []).map((job) => {
            const run = runsByJob[job.id];
            return (
              <div key={job.id} className="bg-white rounded border border-slate-200 p-3" data-testid={`sched-job-${job.id}`}>
                <div className="font-medium text-slate-800">{JOB_LABELS[job.id] || job.id}</div>
                <div className="text-slate-500 mt-1">
                  Prochain : <span className="text-slate-700">{nextRunLabel(job.next_run_at)}</span>
                </div>
                <div className="text-slate-500">
                  Dernier : <span className="text-slate-700">{run ? timeAgo(run.last_run_at) : 'jamais'}</span>
                  {run && run.last_status && (
                    <span className={`ml-1 ${run.last_status === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                      ({run.last_status})
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
